import React from 'react';
import { useTranslation } from 'next-i18next';

import type { Medium, User } from 'src/types/__generated__/graphql';
import useActivityToDisplay from 'src/hooks/useActivityToDisplay';
import useAppRouter from 'src/hooks/useAppRouter';
import { routePathUser } from 'src/constants/router';
import SimpleHorizontalCard from 'src/components/ui/SimpleHorizontalCard';
import Avatar from 'src/components/ui/Avatar';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

interface Props {
    user: Pick<User, 'id' | 'fullName' | 'initials' | 'image' | 'activity' | 'mutualContactsCount' | 'customActivity'>;
    firstSubtitle?: string | 'activity' | null; // use null to not display first subtitle or a custom string to dispaly it as sunbtitle
    secondSubtitle?: string | 'commonRelationsCount' | null;
    rightArea?: React.ReactElement;
    padding?: string;
    minHeight?: string;
    onClick?: () => void;
    dataTargetId?: string;
    backgroundColor?: string;
    cursorPointer?: boolean;
    disableRipple?: boolean;
}

const SimpleHorizontalUserCard: React.FC<Props> = ({
    user,
    firstSubtitle = 'activity',
    secondSubtitle = null,
    rightArea,
    padding = '1rem',
    minHeight,
    onClick,
    dataTargetId,
    backgroundColor,
    cursorPointer = true,
    disableRipple,
}) => {
    const { t } = useTranslation(['common']);
    const router = useAppRouter();
    const { logEventDynamic } = useDynamicLogEvents();
    const getActivityToDisplay = useActivityToDisplay();
    let firstSubtitleContent = null;
    if (firstSubtitle === 'activity') {
        firstSubtitleContent = getActivityToDisplay(user);
    } else if (firstSubtitle) {
        firstSubtitleContent = firstSubtitle;
    }

    let secondSubtitleContent = null;
    if (secondSubtitle === 'commonRelationsCount') {
        secondSubtitleContent = t('profile:commonRelations', {
            count: user.mutualContactsCount ?? 0,
            plurial: user.mutualContactsCount && user.mutualContactsCount > 1 ? 's' : '',
        });
    } else if (secondSubtitle) {
        secondSubtitleContent = secondSubtitle;
    }

    const avatar = <Avatar image={user.image as Medium} alt={user.fullName} fallbackText={user.initials} size={40} />;

    const redirect = async () => {
        logEventDynamic(AnalyticsEvent.SeeUser);
        await router.push(`${routePathUser}/${user.id}`);
    };
    return (
        <SimpleHorizontalCard
            keyIdentifier={user.id}
            firstSubtitle={firstSubtitleContent}
            secondSubtitle={secondSubtitleContent}
            rightArea={rightArea}
            padding={padding}
            minHeight={minHeight}
            onClick={onClick ?? redirect}
            dataTargetId={dataTargetId}
            backgroundColor={backgroundColor}
            avatar={avatar}
            name={user.fullName ?? ''}
            cursorPointer={cursorPointer}
            disableRipple={disableRipple}
        />
    );
};

export default SimpleHorizontalUserCard;
